import { Sparkles } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../components/ui/tooltip';
import { useResponsiveToolbar } from '../../../../components/ui/responsive-toolbar';
import { isAiLauncherDisabled, AI_CLI_REQUIRED_REASON } from '../../../ai-chat/launchers/aiLauncherGate';

interface OptimizeQueryButtonProps {
  onClick: () => void;
  aiCliAvailable?: boolean;
  aiCliDetectionLoading?: boolean;
}

/**
 * "AI: Optimize this query" — R2 (closes G3-F6): moved off the Options
 * popover onto the toolbar itself, right section, between `FormatButton` and
 * `OptionsDropdown` (see `task-R2-brief.md`). Format and Optimize are the
 * truest semantic neighbours — both rewrite the SQL in the editor.
 *
 * Tier 1 collapse, same as `FormatButton` — the label sheds first via
 * `rightCollapseState`, never Execute/Debug/transaction controls or the left
 * group (Plan/Result/Recent/Search). The full "AI: Optimize this query" text
 * lives in the native `title` so it survives icon-only collapse; the
 * Tooltip below exists only to carry `AI_CLI_REQUIRED_REASON` when disabled,
 * since a disabled button gets `disabled:pointer-events-none` (repo
 * convention, see `InstanceToolbar.tsx`'s "AI: Diagnose server load"), which
 * kills a plain `title`.
 */
export function OptimizeQueryButton({ onClick, aiCliAvailable, aiCliDetectionLoading }: OptimizeQueryButtonProps) {
  const { rightCollapseState } = useResponsiveToolbar();
  const isCollapsed = rightCollapseState === 'collapsed';
  const disabled = isAiLauncherDisabled(aiCliAvailable, aiCliDetectionLoading);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Span keeps the tooltip reachable while disabled — a disabled
              button gets pointer-events-none (repo convention). */}
          <span className="inline-block" tabIndex={disabled ? 0 : -1}>
            <Button
              variant="outline"
              size="sm"
              onClick={onClick}
              disabled={disabled}
              title="AI: Optimize this query"
              data-testid="ai-optimize-query-button"
              data-toolbar-item
              className={isCollapsed ? 'px-2' : undefined}
            >
              <Sparkles className="h-4 w-4" />
              {!isCollapsed && 'Optimize'}
            </Button>
          </span>
        </TooltipTrigger>
        {disabled && (
          <TooltipContent side="bottom">
            <p>{AI_CLI_REQUIRED_REASON}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}
